import React, { useState } from "react";
import { Clock, Calendar, AlertTriangle, ShieldCheck, Tag, ArrowRight } from "lucide-react";
import { useApp } from "../../app/context/AppContext";
import { DataTable } from "../../components/common/DataTable";
import { Badge } from "../../components/common/Badge";
import { calculateDaysToExpiry, getExpiryBadgeInfo } from "../../utils/calculations";
import { formatDate, formatCurrency } from "../../utils/formatters";
import { useNavigate } from "react-router-dom";

export function BatchExpiryModule() {
  const { batches, products, warehouses } = useApp();
  const [filterView, setFilterView] = useState("all"); // 'all' | 'critical' | 'expired'
  const navigate = useNavigate();

  // Enriched batch data with countdown days
  const enrichedBatches = batches.map((b) => {
    const days = calculateDaysToExpiry(b.expiryDate);
    const badgeInfo = getExpiryBadgeInfo(days);
    const prod = products.find((p) => p.id === b.productId);
    const wh = warehouses.find((w) => w.id === b.warehouseId);

    return {
      ...b,
      daysToExpiry: days,
      badgeInfo,
      productName: prod?.name || b.productName,
      unit: prod?.unit || "units",
      warehouseName: wh?.name?.split("(")[0]?.trim() || b.warehouseCode
    };
  });

  const filteredData = enrichedBatches.filter((b) => {
    if (filterView === "critical") return b.daysToExpiry > 0 && b.daysToExpiry <= 45;
    if (filterView === "expired") return b.daysToExpiry <= 0;
    return true;
  });

  const columns = [
    {
      header: "Batch No & SKU",
      accessorKey: "batchNo",
      render: (val, row) => (
        <div>
          <span className="font-mono font-bold text-slate-900 dark:text-white">{val}</span>
          <p className="text-[10px] text-slate-400 truncate max-w-[200px]">{row.productName}</p>
        </div>
      )
    },
    {
      header: "Warehouse Location",
      accessorKey: "warehouseName",
      render: (val, row) => (
        <div>
          <p className="font-medium text-slate-800 dark:text-slate-200">{val}</p>
          <span className="text-[10px] font-mono text-emerald-600 dark:text-emerald-400 font-bold">
            Bin: {row.binCode}
          </span>
        </div>
      )
    },
    {
      header: "Mfg Date",
      accessorKey: "mfgDate",
      render: (val) => <span className="font-mono text-slate-500">{formatDate(val)}</span>
    },
    {
      header: "Expiry Date",
      accessorKey: "expiryDate",
      render: (val) => <span className="font-mono text-slate-800 dark:text-slate-200 font-semibold">{formatDate(val)}</span>
    },
    {
      header: "Days to Expiry",
      accessorKey: "daysToExpiry",
      align: "center",
      render: (val, row) => {
        const info = row.badgeInfo;
        return (
          <span
            className={`inline-flex items-center gap-1 font-mono font-bold text-xs px-2.5 py-0.5 rounded-full ${
              info.color === "red"
                ? "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300 animate-pulse"
                : info.color === "amber"
                ? "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300"
                : "bg-emerald-50 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
            }`}
          >
            {info.text}
          </span>
        );
      }
    },
    {
      header: "Qty Left",
      accessorKey: "qtyRemaining",
      align: "right",
      render: (val, row) => (
        <span className="font-mono font-bold text-slate-900 dark:text-white">
          {val} {row.unit}
        </span>
      )
    },
    {
      header: "Action Suggestion",
      accessorKey: "actions",
      sortable: false,
      render: (_, row) => (
        <div className="text-right">
          {row.daysToExpiry <= 0 ? (
            <button
              onClick={() => navigate("/returns")}
              className="px-2 py-0.5 rounded bg-rose-600 text-white text-[10px] font-semibold"
            >
              Write-Off / Return
            </button>
          ) : row.daysToExpiry <= 45 ? (
            <button
              onClick={() => navigate("/sales")}
              className="px-2 py-0.5 rounded bg-amber-500 text-white text-[10px] font-semibold"
            >
              Apply POS Discount
            </button>
          ) : (
            <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">
              Fresh Inventory
            </span>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      {/* Top Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white font-display flex items-center gap-2">
            <Clock className="w-5 h-5 text-emerald-500" />
            Batch Tracking & Expiry Countdown Radar
          </h2>
          <p className="text-xs text-slate-500">
            Real-time expiry countdowns with green/amber/red color triggers, FIFO compliance, and action suggestions
          </p>
        </div>

        {/* Filter Pills */}
        <div className="flex items-center gap-1 p-1 bg-slate-100 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 text-xs">
          <button
            onClick={() => setFilterView("all")}
            className={`px-3 py-1.5 rounded-lg font-semibold transition ${
              filterView === "all" ? "bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-xs" : "text-slate-500 hover:text-slate-800"
            }`}
          >
            All Batches ({enrichedBatches.length})
          </button>
          <button
            onClick={() => setFilterView("critical")}
            className={`px-3 py-1.5 rounded-lg font-semibold transition ${
              filterView === "critical" ? "bg-amber-500 text-white shadow-xs" : "text-slate-500 hover:text-slate-800"
            }`}
          >
            Expiring Soon &lt;45d ({enrichedBatches.filter((b) => b.daysToExpiry > 0 && b.daysToExpiry <= 45).length})
          </button>
          <button
            onClick={() => setFilterView("expired")}
            className={`px-3 py-1.5 rounded-lg font-semibold transition ${
              filterView === "expired" ? "bg-rose-600 text-white shadow-xs" : "text-slate-500 hover:text-slate-800"
            }`}
          >
            Expired Lots ({enrichedBatches.filter((b) => b.daysToExpiry <= 0).length})
          </button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filteredData}
        title="Batch & Expiry Master Ledger"
        subtitle="Sort by Days to Expiry to prioritize oldest stock sales"
        filename="kirana_batch_expiry_ledger"
      />
    </div>
  );
}
